"use client";

import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import ScrollReveal from "@/components/common/ScrollReveal";

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  company: string;
}

const testimonials: Testimonial[] = [
  {
    quote:
      'A majority of traditional security solutions being widely used by almost every control system and SCADA network are completely inadequate to the "offensive security" angle that was clearly demonstrated by ECQ in great depth.',
    author: "Head of Technology Specialist",
    role: "Technology Specialist",
    company: "Leading Provider of Industrial Automation, Test and Measurement Solutions",
  },
  {
    quote:
      "The end work product was superior to many other security reviews I have reviewed. The report was comprehensive, focused on our critical infrastructure and demonstrated a thorough understanding of our business operations and needs.",
    author: "CEO",
    role: "Chief Executive Officer",
    company: "One of the Largest Healthcare Systems in North Carolina",
  },
  {
    quote:
      "The assessment project has been carried out successfully and all of requirements are met. I would like to thank ECQ for their professional services.",
    author: "Head of Technology Service Department",
    role: "Technology Service Department",
    company: "Maybank Kim Eng Securities, Thailand",
  },
];

const AUTO_INTERVAL = 6000;

export default function Customers() {
  const [active, setActive] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Autoplay restarts whenever `active` changes, so a manual pick gets a full
  // interval before the slider moves on by itself.
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setActive((a) => (a + 1) % testimonials.length);
    }, AUTO_INTERVAL);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [active]);

  const t = testimonials[active];

  return (
    <section className="w-full px-4 sm:px-6 lg:px-8 pt-2 pb-12">
      <div className="w-full max-w-[1300px] mx-auto">
        <ScrollReveal direction="up">
          <div className="relative overflow-hidden rounded-md bg-primary">
            {/* Figma: red textured background image (node 751:55908) */}
            <Image
              src="/images/Company/customers_bg.png"
              alt=""
              fill
              aria-hidden
              className="object-cover pointer-events-none"
              unoptimized
            />
            <div className="relative flex flex-col justify-center min-h-[240px] md:min-h-[311px] px-8 md:px-[118px] py-10">
              <div className="flex flex-col items-end gap-6 text-right max-w-[1072px] mx-auto w-full">
                <p className="text-white text-lg md:text-2xl font-medium leading-normal">
                  {t.quote}
                </p>
                <p className="text-text-default text-sm md:text-base font-semibold tracking-[0.5px] leading-6">
                  {t.author}, from a {t.company}
                </p>
              </div>
            </div>
            {/* Offsets are 10px tighter than Figma's 24/54px so the wider tap
                targets keep the dots on the same optical centre line. */}
            <div className="absolute right-5 md:right-[50px] top-1/2 -translate-y-1/2 flex flex-col items-center">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActive(i)}
                  aria-label={`Show testimonial ${i + 1}`}
                  aria-current={i === active}
                  /* 24px tap target so the 4px Figma dot is actually clickable. */
                  className="group flex h-6 w-6 shrink-0 items-center justify-center cursor-pointer"
                >
                  <span
                    className={`rounded-full transition-all duration-300 ${
                      i === active
                        ? "w-4 h-4 border-2 border-white bg-transparent"
                        : "w-1 h-1 bg-white group-hover:w-2 group-hover:h-2"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
